import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { cloneProject, CloneProjectRequest } from '../api/projects';
import { toast } from 'react-toastify';
import { useProjectsPage } from '../state/projects';

interface CloneProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceProject: ProjectInterface | null;
  onCloned: (newProjectId: string) => void;
}

export const CloneProjectModal: React.FC<CloneProjectModalProps> = ({
  isOpen,
  onClose,
  sourceProject,
  onCloned,
}) => {
  const { keycloak } = useKeycloak();
  const { refreshProjects } = useProjectsPage();
  const [formData, setFormData] = useState<CloneProjectRequest>({
    name: '',
    clone_sites: true,
    clone_assets: true,
    clone_project_items: true,
    clone_action_items: false,
    clone_daily_updates: false,
    clone_meeting_notes: false,
    clone_documents: false,
    clone_assignments: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && sourceProject) {
      setFormData({
        name: `Copy of ${sourceProject.name}`,
        clone_sites: true,
        clone_assets: true,
        clone_project_items: true,
        clone_action_items: false,
        clone_daily_updates: false,
        clone_meeting_notes: false,
        clone_documents: false,
        clone_assignments: false,
      });
    }
  }, [isOpen, sourceProject]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
    setNameError(null);
  };

  const handleCheckboxChange = (field: keyof CloneProjectRequest, value: boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceProject || !keycloak.token || !sourceProject.id) return;

    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setNameError('Project name cannot be empty or whitespace only');
      return;
    }

    setIsLoading(true);
    try {
      const response = await cloneProject(keycloak.token, sourceProject.id, { ...formData, name: trimmedName });
      toast.success(`Cloned: ${Object.entries(response.cloned).map(([k, v]) => `${v} ${k}`).join(', ')}`);
      // The sidebar and project list read from the shared store, so refresh it
      // or the new project stays invisible until the next full reload.
      await refreshProjects();
      onCloned(response.new_project_id);
      onClose();
    } catch (e) {
      toast.error('Failed to clone project');
      console.error('Clone error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !sourceProject) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-[#eaeaea]">
          <h2 className="text-lg font-semibold text-[#171717]">Clone Project</h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Name field */}
          <div>
            <label className="block text-sm font-medium text-[#171717] mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              className="w-full px-3 py-2 border border-[#ddd] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Project name"
              required
            />
            {nameError && (
              <p className="text-xs text-red-600 mt-1">{nameError}</p>
            )}
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className="font-medium text-sm text-[#171717]">What to clone:</div>

            <div className="space-y-2 pl-2">
              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_sites}
                  onChange={(e) => handleCheckboxChange('clone_sites', e.target.checked)}
                  className="w-4 h-4"
                />
                Sites
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_assets}
                  onChange={(e) => handleCheckboxChange('clone_assets', e.target.checked)}
                  className="w-4 h-4"
                />
                Assets
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_project_items}
                  onChange={(e) => handleCheckboxChange('clone_project_items', e.target.checked)}
                  className="w-4 h-4"
                />
                Project Items
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_action_items}
                  onChange={(e) => handleCheckboxChange('clone_action_items', e.target.checked)}
                  className="w-4 h-4"
                />
                Action Items
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_daily_updates}
                  onChange={(e) => handleCheckboxChange('clone_daily_updates', e.target.checked)}
                  className="w-4 h-4"
                />
                Daily Updates
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_meeting_notes}
                  onChange={(e) => handleCheckboxChange('clone_meeting_notes', e.target.checked)}
                  className="w-4 h-4"
                />
                Meeting Notes
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_documents}
                  onChange={(e) => handleCheckboxChange('clone_documents', e.target.checked)}
                  className="w-4 h-4"
                />
                Documents
              </label>

              <label className="flex items-center gap-2 text-sm text-[#555]">
                <input
                  type="checkbox"
                  checked={formData.clone_assignments}
                  onChange={(e) => handleCheckboxChange('clone_assignments', e.target.checked)}
                  className="w-4 h-4"
                />
                Assignments
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-[#eaeaea]">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-medium text-[#555] bg-[#f5f5f5] rounded-lg hover:bg-[#eee] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Cloning...' : 'Clone Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
